// Hypothesis math for multi-year return projections (% / $ / cap rate).

export const DEFAULT_RETURN_ASSUMPTIONS = {
  revenue: { mode: 'percent', percent: 2 },
  expense: { mode: 'percent', percent: 2.5 },
  value: { mode: 'percent', percent: 3 },
};

/** Quick presets for simple-mode assumptions (% per year). */
export const RETURN_ASSUMPTION_PRESET_IDS = ['conservative', 'realistic', 'optimistic'];

export const RETURN_ASSUMPTION_PRESETS = {
  conservative: {
    revenue: { mode: 'percent', percent: 1.5 },
    expense: { mode: 'percent', percent: 2.5 },
    value: { mode: 'percent', percent: 2 },
  },
  realistic: {
    revenue: { mode: 'percent', percent: 2 },
    expense: { mode: 'percent', percent: 2.5 },
    value: { mode: 'percent', percent: 3 },
  },
  optimistic: {
    revenue: { mode: 'percent', percent: 3 },
    expense: { mode: 'percent', percent: 2 },
    value: { mode: 'percent', percent: 4 },
  },
};

/**
 * @param {typeof DEFAULT_RETURN_ASSUMPTIONS} assumptions
 * @returns {typeof DEFAULT_RETURN_ASSUMPTIONS}
 */
export function cloneReturnAssumptions(assumptions) {
  return {
    revenue: { ...(assumptions?.revenue || DEFAULT_RETURN_ASSUMPTIONS.revenue) },
    expense: { ...(assumptions?.expense || DEFAULT_RETURN_ASSUMPTIONS.expense) },
    value: { ...(assumptions?.value || DEFAULT_RETURN_ASSUMPTIONS.value) },
  };
}

/**
 * @param {typeof DEFAULT_RETURN_ASSUMPTIONS} assumptions
 * @returns {string | null}
 */
export function matchReturnAssumptionPreset(assumptions) {
  if (!assumptions) return null;
  for (const id of RETURN_ASSUMPTION_PRESET_IDS) {
    const preset = RETURN_ASSUMPTION_PRESETS[id];
    const matches = ['revenue', 'expense', 'value'].every((key) => {
      const a = assumptions[key];
      const p = preset[key];
      if (!a || !p || a.mode !== 'percent' || p.mode !== 'percent') return false;
      return Number(a.percent) === Number(p.percent);
    });
    if (matches) return id;
  }
  return null;
}

export function projectAmount(base, annualRate, years) {
  const b = Number(base) || 0;
  const y = parseInt(years, 10) || 0;
  const r = Number(annualRate) || 0;
  if (y <= 0) return b;
  return b * Math.pow(1 + r, y);
}

export function annualizedRateFromTarget(base, target, years) {
  const b = Number(base) || 0;
  const t = Number(target) || 0;
  const y = parseInt(years, 10) || 0;
  if (y <= 0 || b <= 0 || t <= 0) return 0;
  return Math.pow(t / b, 1 / y) - 1;
}

function parsePercentAssumption(assumption) {
  if (!assumption || assumption.mode !== 'percent') return 0;
  return (Number(assumption.percent) || 0) / 100;
}

function parseAmountAssumption(assumption) {
  if (!assumption || assumption.mode !== 'amount') return 0;
  return Number(assumption.amount) || 0;
}

function resolveRevenueRate(revenueAssumption, baseRevenue, years) {
  if (!revenueAssumption) return 0;
  if (revenueAssumption.mode === 'percent') {
    return parsePercentAssumption(revenueAssumption);
  }
  if (revenueAssumption.mode === 'amount') {
    const target = parseAmountAssumption(revenueAssumption);
    return annualizedRateFromTarget(baseRevenue, target, years);
  }
  return 0;
}

function resolveExpenseRate(expenseAssumption, baseExpense, years) {
  if (!expenseAssumption) return 0;
  if (expenseAssumption.mode === 'percent') {
    return parsePercentAssumption(expenseAssumption);
  }
  if (expenseAssumption.mode === 'amount') {
    const target = parseAmountAssumption(expenseAssumption);
    return annualizedRateFromTarget(baseExpense, target, years);
  }
  return 0;
}

function resolveValueRate(valueAssumption, bases, years, incomeRate, expenseRate) {
  if (!valueAssumption) return 0;
  const purchasePrice = Number(bases.purchasePrice) || 0;
  if (valueAssumption.mode === 'percent') {
    return parsePercentAssumption(valueAssumption);
  }
  if (valueAssumption.mode === 'amount') {
    const target = parseAmountAssumption(valueAssumption);
    return annualizedRateFromTarget(purchasePrice, target, years);
  }
  if (valueAssumption.mode === 'capRate') {
    const capRate = (Number(valueAssumption.capRate) || 0) / 100;
    if (capRate <= 0) return 0;
    const revenueAtYear = projectAmount(
      bases.baseRevenue,
      incomeRate,
      years,
    );
    const expenseAtYear = projectAmount(
      bases.baseExpense,
      expenseRate,
      years,
    );
    const noi = revenueAtYear - expenseAtYear;
    if (noi <= 0) return 0;
    const targetValue = noi / capRate;
    return annualizedRateFromTarget(purchasePrice, targetValue, years);
  }
  return 0;
}

/**
 * @param {{ baseRevenue: number, baseGrossRevenue?: number, baseExpense: number, purchasePrice: number }} bases
 * @param {typeof DEFAULT_RETURN_ASSUMPTIONS} assumptions
 * @param {number} years
 */
export function resolveAssumptionRates({ bases, assumptions, years }) {
  const incomeRate = resolveRevenueRate(
    assumptions?.revenue,
    bases.baseRevenue,
    years,
  );
  const expenseRate = resolveExpenseRate(
    assumptions?.expense,
    bases.baseExpense,
    years,
  );
  const valueRate = resolveValueRate(
    assumptions?.value,
    bases,
    years,
    incomeRate,
    expenseRate,
  );
  return { incomeRate, expenseRate, valueRate };
}

export function valueAtYearFromAssumption(
  valueAssumption,
  bases,
  years,
  incomeRate,
  expenseRate,
) {
  const purchasePrice = Number(bases.purchasePrice) || 0;
  const y = parseInt(years, 10) || 0;
  if (!valueAssumption || y <= 0) return purchasePrice;
  if (valueAssumption.mode === 'percent') {
    const rate = parsePercentAssumption(valueAssumption);
    return projectAmount(purchasePrice, rate, y);
  }
  if (valueAssumption.mode === 'amount') {
    return parseAmountAssumption(valueAssumption);
  }
  if (valueAssumption.mode === 'capRate') {
    const capRate = (Number(valueAssumption.capRate) || 0) / 100;
    if (capRate <= 0) return 0;
    const revenueAtYear = projectAmount(bases.baseRevenue, incomeRate, y);
    const expenseAtYear = projectAmount(bases.baseExpense, expenseRate, y);
    const noi = revenueAtYear - expenseAtYear;
    return noi > 0 ? noi / capRate : 0;
  }
  return purchasePrice;
}

function buildRevenueBreakdownHint({
  grossAtYear,
  effectiveAtYear,
  baseExpense,
  expenseAssumption,
  expenseRate,
  years,
  annualizedPercent,
}) {
  const y = parseInt(years, 10) || 0;
  let expenseAtYear;
  if (expenseAssumption?.mode === 'amount') {
    expenseAtYear = parseAmountAssumption(expenseAssumption);
  } else {
    expenseAtYear = projectAmount(Number(baseExpense) || 0, expenseRate, y);
  }
  return {
    kind: 'revenueBreakdown',
    grossAtYear,
    effectiveAtYear,
    expenseAtYear,
    netAtYear: effectiveAtYear - expenseAtYear,
    ...(annualizedPercent != null ? { annualizedPercent } : {}),
  };
}

/**
 * @returns {{ revenue: object, expense: object, value: object }}
 */
export function computeDisplayHints({ bases, assumptions, years }) {
  const y = parseInt(years, 10) || 0;
  const resolved = resolveAssumptionRates({ bases, assumptions, years });
  const hints = { revenue: null, expense: null, value: null };

  if (assumptions?.revenue?.mode === 'percent') {
    const effectiveAtYear = projectAmount(
      bases.baseRevenue,
      resolved.incomeRate,
      y,
    );
    const baseGross = Number(bases.baseGrossRevenue) || Number(bases.baseRevenue) || 0;
    const grossAtYear = projectAmount(baseGross, resolved.incomeRate, y);
    hints.revenue = buildRevenueBreakdownHint({
      grossAtYear,
      effectiveAtYear,
      baseExpense: bases.baseExpense,
      expenseAssumption: assumptions.expense,
      expenseRate: resolved.expenseRate,
      years: y,
    });
  } else if (assumptions?.revenue?.mode === 'amount') {
    const baseEffective = Number(bases.baseRevenue) || 0;
    const baseGross = Number(bases.baseGrossRevenue) || baseEffective;
    const effectiveAtYear = parseAmountAssumption(assumptions.revenue);
    const grossRatio = baseEffective > 0 ? baseGross / baseEffective : 1;
    hints.revenue = buildRevenueBreakdownHint({
      grossAtYear: effectiveAtYear * grossRatio,
      effectiveAtYear,
      baseExpense: bases.baseExpense,
      expenseAssumption: assumptions.expense,
      expenseRate: resolved.expenseRate,
      years: y,
      annualizedPercent: resolved.incomeRate * 100,
    });
  }

  if (assumptions?.expense?.mode === 'percent') {
    hints.expense = {
      kind: 'amountAtYear',
      amountAtYear: projectAmount(
        bases.baseExpense,
        resolved.expenseRate,
        y,
      ),
    };
  } else if (assumptions?.expense?.mode === 'amount') {
    hints.expense = {
      kind: 'annualizedPercent',
      percent: resolved.expenseRate * 100,
    };
  }

  if (assumptions?.value?.mode === 'percent') {
    hints.value = {
      kind: 'amountAtYear',
      amountAtYear: projectAmount(
        bases.purchasePrice,
        resolved.valueRate,
        y,
      ),
    };
  } else if (assumptions?.value?.mode === 'amount') {
    hints.value = {
      kind: 'annualizedPercent',
      percent: resolved.valueRate * 100,
    };
  } else if (assumptions?.value?.mode === 'capRate') {
    const amountAtYear = valueAtYearFromAssumption(
      assumptions.value,
      bases,
      y,
      resolved.incomeRate,
      resolved.expenseRate,
    );
    hints.value = {
      kind: 'capRateDerived',
      amountAtYear,
      annualizedPercent: resolved.valueRate * 100,
    };
  }

  return hints;
}

export function assumptionsFromPercentTriplet(income, expense, value) {
  return {
    revenue: { mode: 'percent', percent: Number(income) || 0 },
    expense: { mode: 'percent', percent: Number(expense) || 0 },
    value: { mode: 'percent', percent: Number(value) || 0 },
  };
}

export function assumptionsFromRefinancingSubScenario(sub) {
  return {
    revenue: {
      mode: 'percent',
      percent: parseFloat(sub?.revenueGrowthPct) || 0,
    },
    expense: {
      mode: 'percent',
      percent: parseFloat(sub?.expenseGrowthPct) || 0,
    },
    value: {
      mode: 'percent',
      percent: parseFloat(sub?.valueAppreciationPct) || 0,
    },
  };
}

/** Human-readable lines for PDF (FR/EN handled by caller with keys). */
export function buildAssumptionSummaryLines(assumptions, years, locale = 'fr-CA') {
  const fmtMoney = (n) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
    }).format(n);
  const fmtPct = (n) =>
    `${(Number(n) || 0).toLocaleString(locale, { maximumFractionDigits: 2 })}%`;

  const lines = [];
  const y = years;

  const rev = assumptions?.revenue;
  if (rev?.mode === 'percent') {
    lines.push({ key: 'revenue', text: `${fmtPct(rev.percent)}/yr` });
  } else if (rev?.mode === 'amount') {
    lines.push({ key: 'revenue', text: `${fmtMoney(rev.amount)} @ Y${y}` });
  }

  const exp = assumptions?.expense;
  if (exp?.mode === 'percent') {
    lines.push({ key: 'expense', text: `${fmtPct(exp.percent)}/yr` });
  } else if (exp?.mode === 'amount') {
    lines.push({ key: 'expense', text: `${fmtMoney(exp.amount)} @ Y${y}` });
  }

  const val = assumptions?.value;
  if (val?.mode === 'percent') {
    lines.push({ key: 'value', text: `${fmtPct(val.percent)}/yr` });
  } else if (val?.mode === 'amount') {
    lines.push({ key: 'value', text: `${fmtMoney(val.amount)} @ Y${y}` });
  } else if (val?.mode === 'capRate') {
    lines.push({ key: 'value', text: `TGA ${fmtPct(val.capRate)}` });
  }

  return lines;
}

export const OUTLOOK_VALUES = ['pessimistic', 'realistic', 'optimistic'];

export const MAX_RETURN_HORIZON_YEARS = 30;

/** @typedef {{ mode: 'percent'|'amount', percent?: number, amount?: number } | null} AdvancedCell */

/**
 * @param {number} years
 * @returns {{ years: number, rows: Array<{ revenue: AdvancedCell, expense: AdvancedCell, value: AdvancedCell }> }}
 */
export function createEmptyAdvancedSchedule(years) {
  const n = Math.min(Math.max(parseInt(years, 10) || 1, 1), MAX_RETURN_HORIZON_YEARS);
  return {
    years: n,
    rows: Array.from({ length: n }, () => ({
      revenue: null,
      expense: null,
      value: null,
    })),
  };
}

/**
 * Resize schedule when horizon changes; preserve existing row data.
 */
export function resizeAdvancedSchedule(schedule, newYears) {
  const n = Math.min(Math.max(parseInt(newYears, 10) || 1, 1), MAX_RETURN_HORIZON_YEARS);
  const prev = schedule?.rows || [];
  const rows = Array.from({ length: n }, (_, i) => {
    if (i < prev.length) {
      return {
        revenue: prev[i]?.revenue ?? null,
        expense: prev[i]?.expense ?? null,
        value: prev[i]?.value ?? null,
      };
    }
    return { revenue: null, expense: null, value: null };
  });
  return { years: n, rows };
}

/**
 * Resolve one year's amount from a cell (YoY % or absolute $).
 * @param {AdvancedCell} cell
 * @param {number} previousResolved
 */
export function resolveAdvancedCell(cell, previousResolved) {
  const prev = Number(previousResolved) || 0;
  if (!cell) return prev;
  if (cell.mode === 'amount') {
    return Number(cell.amount) || 0;
  }
  if (cell.mode === 'percent') {
    const rate = (Number(cell.percent) || 0) / 100;
    return prev * (1 + rate);
  }
  return prev;
}

/**
 * Implied YoY % from previous to resolved amount.
 */
export function impliedYoYPercent(previousResolved, resolved) {
  const prev = Number(previousResolved) || 0;
  const cur = Number(resolved) || 0;
  if (prev <= 0) return 0;
  return ((cur / prev) - 1) * 100;
}

/**
 * @param {{ baseRevenue: number, baseExpense: number, purchasePrice: number }} bases
 * @param {{ years: number, rows: Array<{ revenue: AdvancedCell, expense: AdvancedCell, value: AdvancedCell }> }} schedule
 * @returns {{ revenue: number[], expenses: number[], values: number[], propertyValueAtYear: number, resolvedRows: Array<{ year: number, revenue: number, expense: number, value: number, netIncome: number }> }}
 */
export function resolveAdvancedYearProfile(bases, schedule) {
  const n = Math.min(
    Math.max(parseInt(schedule?.years, 10) || 0, 0),
    MAX_RETURN_HORIZON_YEARS,
  );
  const rows = schedule?.rows || [];
  let revenue = Number(bases.baseRevenue) || 0;
  let expense = Number(bases.baseExpense) || 0;
  let value = Number(bases.purchasePrice) || 0;

  const revenueOut = [];
  const expensesOut = [];
  const valuesOut = [];
  const resolvedRows = [];

  for (let i = 0; i < n; i++) {
    const row = rows[i] || {};
    revenue = resolveAdvancedCell(row.revenue, revenue);
    expense = resolveAdvancedCell(row.expense, expense);
    value = resolveAdvancedCell(row.value, value);
    revenueOut.push(revenue);
    expensesOut.push(expense);
    valuesOut.push(value);
    resolvedRows.push({
      year: i + 1,
      revenue,
      expense,
      value,
      netIncome: revenue - expense,
    });
  }

  return {
    revenue: revenueOut,
    expenses: expensesOut,
    values: valuesOut,
    propertyValueAtYear: n > 0 ? valuesOut[n - 1] : value,
    resolvedRows,
  };
}

/**
 * Build advanced schedule rows that reproduce simple-mode compound growth (all null = inherit chain from simple rates per row).
 * Stores explicit percent cells per year derived from simple assumptions.
 */
export function buildAdvancedScheduleFromSimple(bases, assumptions, years) {
  const n = Math.min(Math.max(parseInt(years, 10) || 1, 1), MAX_RETURN_HORIZON_YEARS);
  const resolved = resolveAssumptionRates({ bases, assumptions, years: n });
  const incomePct = resolved.incomeRate * 100;
  const expensePct = resolved.expenseRate * 100;
  const valuePct = resolved.valueRate * 100;

  const rows = Array.from({ length: n }, () => ({
    revenue: { mode: 'percent', percent: incomePct },
    expense: { mode: 'percent', percent: expensePct },
    value: { mode: 'percent', percent: valuePct },
  }));

  if (assumptions?.revenue?.mode === 'amount') {
    rows[n - 1].revenue = { mode: 'amount', amount: assumptions.revenue.amount };
    for (let i = 0; i < n - 1; i++) rows[i].revenue = { mode: 'percent', percent: incomePct };
  }
  if (assumptions?.expense?.mode === 'amount') {
    rows[n - 1].expense = { mode: 'amount', amount: assumptions.expense.amount };
    for (let i = 0; i < n - 1; i++) rows[i].expense = { mode: 'percent', percent: expensePct };
  }
  if (assumptions?.value?.mode === 'amount') {
    rows[n - 1].value = { mode: 'amount', amount: assumptions.value.amount };
    for (let i = 0; i < n - 1; i++) rows[i].value = { mode: 'percent', percent: valuePct };
  } else if (assumptions?.value?.mode === 'capRate') {
    const valAtN = valueAtYearFromAssumption(
      assumptions.value,
      bases,
      n,
      resolved.incomeRate,
      resolved.expenseRate,
    );
    rows[n - 1].value = { mode: 'amount', amount: valAtN };
    for (let i = 0; i < n - 1; i++) rows[i].value = { mode: 'percent', percent: valuePct };
  }

  return { years: n, rows };
}

/** Count rows with at least one non-null cell override. */
export function countAdvancedOverrides(schedule) {
  const rows = schedule?.rows || [];
  return rows.filter(
    (r) => r?.revenue != null || r?.expense != null || r?.value != null,
  ).length;
}

/** Build PDF table rows for advanced mode. */
export function buildAdvancedSchedulePdfRows(bases, schedule, locale = 'fr-CA') {
  const profile = resolveAdvancedYearProfile(bases, schedule);
  const fmtMoney = (n) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
    }).format(n);
  return profile.resolvedRows.map((r) => ({
    year: r.year,
    revenue: fmtMoney(r.revenue),
    expense: fmtMoney(r.expense),
    value: fmtMoney(r.value),
    netIncome: fmtMoney(r.netIncome),
  }));
}
